import React, { memo, Fragment, useCallback, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import Card from "../../../components/bootstrap/card";
import useUserMe from "../../../hooks/useUserMe";
import useConfigProgramList from "../../../hooks/config/useConfigProgramList";
import { formatDate2 } from "../../../utilities/dateFormatter";
import { formatNumber } from "../../../utilities/formatNumber";
import { deleteConfigProgram, updateConfigProgramSortOrder } from "../../../api/config";
import DeleteIcon from "../../../components/icon/DeleteIcon";
import EditIcon from "../../../components/icon/EditIcon";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from "react-toastify";

function DraggableRow({ item, idx, onDelete, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id
  });
  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? '#f0f4ff' : '',
      }}
      {...props}
    >
      <td {...attributes}>
        <span
          {...listeners}
          style={{ cursor: 'grab', display: 'inline-block', touchAction: 'none', userSelect: 'none', fontSize: 20 }}
          aria-label="순서 변경"
        >
          &#9776;
        </span>
        <span style={{ marginLeft: 8 }}>{idx + 1}</span>
      </td>
      <td>{item.name}</td>
      <td>{formatNumber(item.price)}</td>
      <td>{formatDate2(item.createdAt)}</td>
      <td>{formatDate2(item.updatedAt)}</td>
      <td>
        <div className="flex align-items-center list-user-action">
          <a className="btn btn-sm btn-icon btn-warning edit-btn" href={`/config/program/${item.id}`} aria-label="Edit">
            <span className="btn-inner">
              <EditIcon />
            </span>
          </a>
          {" "}
          <button type="button" className="btn btn-sm btn-icon btn-danger delete-btn" aria-label="Delete" onClick={() => onDelete(item.id)}>
            <span className="btn-inner">
              <DeleteIcon />
            </span>
          </button>
        </div>
      </td>
    </tr>
  );
}

const ConfigProgram = memo(() => {
  const { data: userMeData } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;
  const { programList = [], refresh } = useConfigProgramList(storeId);
  const [items, setItems] = useState([]);

  React.useEffect(() => {
    setItems(programList);
  }, [programList]);

  const handleDelete = useCallback(async (id) => {
    const confirmDelete = window.confirm("정말 삭제하시겠습니까?");
    if (confirmDelete) {
      try {
        await deleteConfigProgram(id);
        alert("삭제되었습니다.");
        refresh();
      } catch (error) {
        alert(error.message);
      }
    }
  }, [refresh]);

  const handleCreatePage = () => {
    window.location.href = '/config/program/add';
  };

  // dnd-kit 센서
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // 드래그 앤 드롭 순서 변경
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    try {
      await updateConfigProgramSortOrder(storeId, reordered.map(item => item.id));
      toast.success("변경이 완료되었습니다.");
      refresh();
    } catch (e) {
      toast.error(e.message || "순서 변경에 실패했습니다.");
    }
  };

  return (
    <Fragment>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">프로그램 설정</h4>
              </div>
              <div>
                <Button type="button" variant="btn btn-primary" onClick={handleCreatePage}>
                  추가
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="px-0">
              <div className="table-responsive">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <table className="table custom-table movie_table">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 60 }}>순서</th>
                          <th>프로그램명</th>
                          <th>금액</th>
                          <th>등록일</th>
                          <th>수정일</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <DraggableRow key={item.id} item={item} idx={idx} onDelete={handleDelete} />
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
});

ConfigProgram.displayName = "ConfigProgram";
export default ConfigProgram;
